<i18n>
{
	"en": {
		"etd": "Departure",
		"editTrip": "Edit trip",
		"from": "From",
		"status": "Status",
		"to": "To",
		"tripNumber": "Trip nr.",
		"type": "Type",
		"noTripsFound": "No trips were found that match this search. Try expanding your search criteria.",
		"tripsFound": "No trips found | {count} trip found | {count} trips found",
		"newTrip": "New trip",
		"vendorName": "Vendor",
		"vendorNumber": "Vendor No.",
		"orders": "Orders",
		"loadingMeters": "Loading",
		"volume": "Volume",
		"grossWeight": "Gr. Weight",
		"wasSent": "Sent",
		"yes": "Yes",
		"no": "No",
		"sendTripOnEdi" : "EDI",
		"edi" : "EDI",
		"loadingList": "Loading List",
		"loadingListLandscape": "Landscaped Loading List",
		"status/cancelled": "Cancelled",
		"status/draft": "Draft",
		"status/ready": "Ready",
		"status/planned": "Planned",
		"status/rejected": "Rejected",
		"status/unsuccessful": "Unsuccessful",
		"status/requested": "Requested",
		"status/received": "Received",
		"status/accepted": "Accepted",
		"status/readyForPickup": "Ready for Pickup",
		"status/loaded": "Loaded",
		"status/unloaded": "Unloaded"
	},
	"no": {
		"etd": "Avgang",
		"editTrip": "Rediger tur",
		"from": "Fra",
		"status": "Status",
		"to": "Til",
		"tripNumber": "Turnr.",
		"type": "Type",
		"noTripsFound": "Vi fant ingen turer som matcher dette søket. Prøv å utvide søkekriteriene.",
		"tripsFound": "Ingen turer | {count} tilgjengelig tur | {count} turer",
		"newTrip": "Ny tur",
		"vendorName": "Vendor",
		"vendorNumber": "Vendor Nr.",
		"orders": "Oppdr.",
		"loadingMeters": "Lastm.",
		"volume": "Volum",
		"grossWeight": "Br. vekt",
		"wasSent": "Sendt",
		"yes": "Ja",
		"no": "Nei",
		"sendTripOnEdi" : "EDI",
		"edi" : "EDI",
		"loadingList": "Lasteliste",
		"loadingListLandscape": "Lasteliste landskap",
		"status/cancelled": "Annullert",
		"status/draft": "Kladd",
		"status/ready": "Klar",
		"status/planned": "Planlagt",
		"status/rejected": "Avslått",
		"status/unsuccessful": "Bomtur",
		"status/requested": "Forespurt",
		"status/received": "Mottatt",
		"status/accepted": "Akseptert",
		"status/readyForPickup": "Klar for henting",
		"status/loaded": "Lastet",
		"status/unloaded": "Losset"
	}
}
</i18n>
<template>
	<t-resizable-vertical-panel
		class="trip-list"
		:showLowerPanel="singleTripSelected"
		:disabled="!singleTripSelected"
		:localStorageId="computedStoreNamespace"
		:upperPercentage="75"
	>
		<div slot="header" class="trip-list-header">
			<t-panel-header icon="trip">
				{{ $tc('tripsFound', tripsFound) }}
			</t-panel-header>
			<div
				v-show="subHeaderWidthStage !== 'small' && tripsFound"
				class="trip-list-summary"
			>
				<t-metric-details
					:value="totalVolume"
					decimals="1"
					hide-pie-icon
					unit="m3"
				/>
				<t-metric-details
					:value="totalWeight"
					hide-pie-icon
					unit="kg"
				/>
				<t-metric-details
					:value="totalLoadingMeters"
					decimals="1"
					hide-pie-icon
					unit="lm"
				/>
			</div>

			<t-icon-button
				v-if="!filtersVisible"
				icon="search"
				color="blue"
				type="bp"
				style="margin-right: 5px"
				@click="$emit('search')"
			/>
			<t-icon-button
				v-if="!filtersVisible"
				icon="filter-open"
				color="blue"
				type="bp"
				style="margin-right: 15px"
				@click="$emit('toggleFilters')"
			/>
		</div>

		<div slot="sub-header" ref="subHeader" class="trip-list-sub-header">
			<template>
				<div class="button-group-left">
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'small'"
						:value="$t('newTrip')"
						appearance="minimal"
						type="bp"
						iconBefore="plus"
						@click="openNewTripModal"
					/>

					<t-icon-button
						:shrink="subHeaderWidthStage === 'small'"
						:disabled="!tripSendable"
						appearance="minimal"
						type="bp"
						icon="edi"
						@click="$emit('sendTripOrdersOnEdi')"
					/>
				</div>

				<div class="button-group-right">
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'small' || subHeaderWidthStage === 'medium'"
						:value="$t('loadingList')"
						:disabled="!singleTripSelected"
						appearance="minimal"
						type="bp"
						iconBefore="waybill"
						@click="showLoadingList"
					/>
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'small' || subHeaderWidthStage === 'medium'"
						:value="$t('loadingListLandscape')"
						:disabled="!singleTripSelected"
						appearance="minimal"
						type="bp"
						iconBefore="label"
						@click="showLoadingListLandscape"
					/>
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'small'|| subHeaderWidthStage === 'medium'"
						:value="$t('editTrip')"
						:disabled="!editableTrip"
						appearance="minimal"
						width="120px"
						type="bp"
						iconBefore="annotation"
						@click="onEditTrip()"
					/>
				</div>
			</template>
		</div>
		<template slot="upper">
			<div class="trip-list-content">
				<t-grid
					v-if="tripsFound"
					ref="grid"
					v-droppable:triplist="handleTripDrop"
					:columnDefinitions="columnDefinitions"
					:defaultColumnDefinition="defaultColumnDefinition"
					:rowHeight="50"
					:items="trips"
					:selectableMode="['row']"
					:localStorageId="localStorageId"
					:selectedIds="selectedIds"
					:enableDrop="dragging"
					selectable
					singleSelect
					@selected="onRowsSelected"
					@rowDoubleClick="onEditTrip"
					@enter="onEditTrip"
				>
					<template v-slot:tripNumber="{ item }">
						<div v-if="item.type.internalCode === 'external'">
							{{ item.vendor.name }}
						</div>
						<div v-else>
							{{ item.tripNumber }}
						</div>
					</template>
					<template v-slot:status="{ item }">
						<div v-if="item.type.internalCode === 'external'" style="text-align:center">
							<t-timpex-icon
								icon="orders"
								size="md"
							/>
						</div>
						<div v-else style="text-align:center">
							<div v-if="item.cargoCarrier">
								<t-timpex-icon
									:icon="getStatusIcon(item)"
									:color="getStatusColor(item)"
									:title="getStatusTitle(item)"
									size="md"
								/>
							</div>
							<div v-if="item.cargoCarrier">
								{{ item.cargoCarrier.registrationNumber }}
							</div>
						</div>
					</template>
					<template v-slot:type="{ item }">
						<t-timpex-icon
							:icon="getTypeIcon(item)"
							size="sm"
							type="bp"
							color="blue"
							style="padding-left: 3px;"
						/>
					</template>
					<template v-slot:edi="{ item }">
						<div v-if="item.edi">

							<t-timpex-icon
								v-if="tripQueuedForEdi(item)"
								icon="time"
								size="sm"
								type="bp"
								color="orange"
								style="padding-left: 3px;"
							/>
							<t-timpex-icon
								v-else-if="tripSentOnEdi(item)"
								icon="tick"
								size="sm"
								type="bp"
								color="green"
								style="padding-left: 3px;"
							/>
							<t-timpex-icon
								v-else-if="tripSentOnEdiWithErrors(item)"
								icon="order-warning"
								size="sm"
								color="red"
								style="padding-left: 3px;"
							/>

						</div>

					</template>
					<template v-slot:fromAddress="{ item }">
						<template v-if="item.fromAddress">
							<div style="height: 16px;">{{ item.fromAddress.name }}</div>
							<div class="grid-cell-sub-line">{{ item.fromAddress.street }}, {{ item.fromAddress.postalCode }} {{ item.fromAddress.postalPlace }}</div>
						</template>
					</template>
					<template v-slot:toAddress="{ item }">
						<template v-if="item.toAddress">
							<div style="height: 16px;">{{ item.toAddress.name }}</div>
							<div class="grid-cell-sub-line">{{ item.toAddress.street }}, {{ item.toAddress.postalCode }} {{ item.toAddress.postalPlace }}</div>
						</template>
					</template>
					<template v-slot:orders="{ value }" class="">
						<div class="grid-cell-center">
							<t-timpex-icon
								icon="order"
								size="sm"
								style="padding-bottom: 3px;"
							/>
						</div>
						<div class="grid-cell-bold grid-cell-center">{{ value }}</div>
					</template>

					<template v-slot:volume="{ item }" class="">
						<div v-if="item.type.internalCode !== 'external'">
							<t-metric-details
								:value="item.volume"
								:max="getMaxVolume(item)"
								decimals="1"
								vertical
								center
							/>
						</div>
					</template>
					<template v-slot:grossWeight="{ item }" class="">
						<div v-if="item.type.internalCode !== 'external'">
							<t-metric-details
								:value="item.grossWeight"
								:max="getMaxWeight(item)"
								vertical
								center
							/>
						</div>
					</template>
					<template v-slot:loadingMeters="{ item }" class="">
						<div v-if="item.type.internalCode !== 'external'">
							<t-metric-details
								:value="item.loadingMeters"
								:max="getMaxLoadingMeters(item)"
								decimals="1"
								vertical
								center
							/>
						</div>
					</template>
				</t-grid>
				<t-no-content-panel v-else :text="$t('noTripsFound')" icon="truck-front" />
			</div>
			<t-overlay
				:isShown="tripsLoading"
				loaderMarginBottom="50px"
				absolute
				loader
			/>
		</template>
		<template
			slot="lower"
		>
			<t-pane
				height="calc(100% - 48px)"
				border="default"
				background="tint2"
				borderRadius="5px"
				class="cargo-pane"
			>
				<CargoInformation
					v-droppable:triplist="handleCargoDrop"
					:tripDetails="tripDetails"
					@delete="$emit('cargoInfoDelete',$event)"
					@moveOrder="$emit('cargoInfoMove',$event)"
					@scheduleOrderForEdi="$emit('cargoInfoScheduleOrderEdi', $event)"
					@unscheduleOrderForEdi="$emit('cargoInfoUnscheduleOrderEdi', $event)"
					@packageLabel="$emit('packageLabel', $event)"
					@waybill="$emit('waybill', $event)"
					@transportInstruction="$emit('transportInstruction', $event)"
					@removeOrderAndMerge="$emit('removeOrderAndMerge', $event)"
					@removeOrder="$emit('removeOrder', $event)"
				/>
			</t-pane>
		</template>
	</t-resizable-vertical-panel>
</template>

<script>
import { mapDynamicState, mapDynamicActions } from '@/core/mapDynamic';
import {
	eventBus, displayDateFormat, storageDateFormat, displayDateFormatter, dateExists, statuses, tripTypeIcon, cargoCarrierIcon, sumValues
} from '@/core';
import moment from 'moment';
import CargoInformation from './CargoInformation';
import TIconButton from '@/components/evergreen/buttons/TIconButton';
import TShrinkableButton from '@/components/evergreen/buttons/TShrinkableButton';
import TResizableVerticalPanel from '@/components/evergreen/timpex/layers/TResizableVerticalPanel';
import TMetricDetails from '@/components/evergreen/timpex/layers/TMetricDetails';

export default {
	name: 'TripList',
	components: { TMetricDetails, TResizableVerticalPanel, TShrinkableButton, TIconButton, CargoInformation },
	props: {
		filtersVisible: Boolean,
		availableWidth: Number,
	},
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			subHeaderObserver: null,
			subHeaderWidth: null
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace',[ 'trips', 'tripsLoading', 'tripDetails' ]),
		searchId() {
			return this.$route.params.searchId || -1;
		},
		subHeaderWidthStage() {

			let stage = 'large';

			switch (this.$i18n.locale) {

			case 'no':
			case 'nb-NO':

				if (this.availableWidth < 290) {
					stage = 'small';

				} else if (this.availableWidth < 558)
					stage = 'medium';

				break;
			case 'en':
			case 'en-US':
			case 'en-GB':

				if (this.availableWidth < 320)
					stage = 'small';

				else if (this.availableWidth < 622)
					stage = 'medium';

				break;
			default:
				stage = 'large';

			}

			return stage;
		},
		computedStoreNamespace() {
			return `loadPlanning/${this.searchId}/tripSearch`;
		},
		dragging() {
			return this.$store.getters['draggable/isDragging'];
		},
		tripSendable() {
			return this.editableTrip && Boolean(this.tripDetails.vendor.edipartId);
		},
		localStorageId() {
			return `loadPlanningTripsSearchList/${this.searchId}`;
		},
		tripsFound() {
			return this.trips.length;
		},
		singleTripSelected() {
			return this.selectedIds.length === 1;
		},
		editableTrip() {
			return this.singleTripSelected && this.tripDetails && this.tripDetails.type.internalCode !== 'external';
		},
		totalVolume() {
			return sumValues(this.trips, 'volume');
		},
		totalWeight() {
			return sumValues(this.trips, 'grossWeight');
		},
		totalLoadingMeters() {
			return sumValues(this.trips, 'loadingMeters');
		},
		selectedIds() {
			return this.tripDetails ? [this.tripDetails.id] : [];
		},
		columnDefinitions() {
			return [
				{
					field: 'tripNumber',
					displayName: this.$t('tripNumber'),
					width: 75,
					type: 'number',
					comparableValueGetter: (item) => Number(item.tripNumber),
					sortDirection: 'desc'
				},
				{
					field: 'edi',
					displayName: this.$t('edi'),
					resizable: false,
					valueGetter: (item) => item.name,
					width: 100,
					center: true,
				},
				{
					field: 'status',
					displayName: this.$t('status'),
					valueGetter: (item) => item.name,
					resizable: false,
					width: 80,
				},
				{
					field: 'type',
					displayName: this.$t('type'),
					valueGetter: (item) => item.name,
					resizable: false,
					width: 40,
					center: true,
				},
				{
					field: 'etd.date',
					displayName: this.$t('etd'),
					valueGetter: (item) => displayDateFormatter(this.dateGetter(item.atd, item.etd)),
					width: 90,
					resizable: false,
					type: 'date',
					dateFormat: displayDateFormat,
					comparableValueGetter: (item) => moment(this.dateGetter(item.atd, item.etd), storageDateFormat).startOf('day').toISOString(),
					cellClass(item) { return dateExists(item.atd) ? 'grid-cell-bold' : 'grid-cell-light'; }
				},
				{
					field: 'fromAddress',
					displayName: this.$t('from'),
					comparableValueGetter: (item) => {
						if (item.type.internalCode === 'external') return '';
						return this.addressGetter(item.fromAddress);
					},
					type: 'address',
					minWidth: 125,
				},
				{
					field: 'toAddress',
					displayName: this.$t('to'),
					comparableValueGetter: (item) => {
						if (item.type.internalCode === 'external') return '';
						return this.addressGetter(item.toAddress);
					},
					type: 'address',
					minWidth: 125,
				},
				{
					field: 'vendor.name',
					displayName: this.$t('vendorName'),
					valueGetter: (item) => item.vendor ? item.vendor.name : ''
				},
				{
					field: 'vendor.vendorNumber',
					displayName: this.$t('vendorNumber'),
					valueGetter: (item) => item.vendor ? item.vendor.vendorNumber : ''
				},
				{
					field: 'orders',
					displayName: this.$t('orders'),
					valueGetter: (item) => item.orders ? item.orders.length : 0,
					type: 'number',
					width: 50
				},
				{
					field: 'volume',
					displayName: this.$t('volume'),
					align: 'left',
					type: 'number',
					width: 80
				},
				{
					field: 'grossWeight',
					displayName: this.$t('grossWeight'),
					align: 'left',
					type: 'number',
					width: 80
				},
				{
					field: 'loadingMeters',
					displayName: this.$t('loadingMeters'),
					align: 'left',
					type: 'number',
					width: 80
				},
				{
					field: 'wasSent',
					displayName: this.$t('wasSent'),
					valueGetter: item => item.wasSent ? this.$t('yes') : this.$t('no'),
				},
			];
		}
	},
	async mounted() {
		await this.$nextTick();
		let { subHeader } = this.$refs;

		if (subHeader) {
			this.subHeaderObserver = new ResizeObserver(entries => {

				this.subHeaderWidth = entries[0].contentRect.width;
			});
			this.subHeaderObserver.observe(subHeader);
		}

	},
	beforeDestroy() {
		if (this.subHeaderObserver) this.subHeaderObserver.disconnect();
	},

	methods: {
		...mapDynamicActions('computedStoreNamespace', [
			'setTripDetails',
			'sendTripOnEdi',
			'getLoadingList'
		]),
		scheduleTripForEdi() {
			if (this.tripDetails)
				this.sendTripOnEdi();
		},
		handleTripDrop(orders) {
			let trip = this.$refs.grid.getHoveredItem();
			this.handleDrop(orders, trip);
		},
		handleCargoDrop(orders) {
			this.handleDrop(orders, this.tripDetails);
		},
		handleDrop(orders, trip) {
			if (!Array.isArray(orders)) {
				orders = [orders];
			}
			if (trip.type.internalCode === 'external') {
				this.$emit('showAddToTransporter', { trip: trip, orders: orders });
			} else {
				this.$emit('showAddToTrip', { trip: trip, orders: orders });
			}
		},
		addressGetter(party) {
			if (typeof party === 'string') return party;
			if (!party) return '';
			return `${party.name} ${party.street} ${party.postalCode} ${party.postalPlace}`;
		},
		dateGetter(actual, estimated) {
			if (dateExists(actual)) return actual.date;
			if (dateExists(estimated)) return estimated.date;
			return undefined;
		},
		onRowsSelected() {
			let [item] = this.$refs.grid.getSelectedItems();
			if (!item) {
				this.setTripDetails(null);
				return;
			}
			this.setTripDetails(item.id);
		},
		getStatusIcon(item) {
			if (!item.cargoCarrier) return 'truck';
			return cargoCarrierIcon.getIcon(item.cargoCarrier.iconId);
		},
		getStatusColor(item) {
			return statuses.color(item.status);
		},
		getStatusTitle(item) {
			return this.$t(`status/${statuses.operational(item.status)}`);
		},
		getTypeIcon(item) {
			return tripTypeIcon(item.type.internalCode);
		},
		tripQueuedForEdi(item) {
			let ediOrders = item.orders.filter(x => x.edi);
			return ediOrders.some(x => x.edi.outboundSchedule.isScheduled && !x.edi.outboundSchedule.isSent && !x.edi.outboundSchedule.failed);
		},
		tripSentOnEdi(item) {
			let ediOrders = item.orders.filter(x => x.edi);
			let scheduledAndCreated = ediOrders.some(x => !x.edi.outboundSchedule.isScheduled && x.edi.outboundSchedule.isSent);
			let errorsInBatch = ediOrders.some(x => x.edi.outboundSchedule.failed);
			return scheduledAndCreated && !errorsInBatch;
		},
		tripSentOnEdiWithErrors(item) {
			let ediOrders = item.orders.filter(x => x.edi);
			let scheduledAndCreated = ediOrders.some(x => x.edi.outboundSchedule.isScheduled && !x.edi.outboundSchedule.isSent);
			let errorsInBatch = ediOrders.some(x => x.edi.outboundSchedule.failed);
			console.log(ediOrders);
			return scheduledAndCreated && errorsInBatch;
		},
		onEditTrip(id) {
			let trip = undefined;
			if (id) {
				trip = this.trips.find(x => x.id === id);
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				trip = selectedItems[0];
			}
			if (!trip) return;
			// TODO: Separate editing for external trips
			if (trip.type.internalCode === 'external') return;
			eventBus.$emit('setEditTrip',trip.id);
			eventBus.$emit('toggleNewTripModal',true);
		},
		openNewTripModal() {
			eventBus.$emit('toggleNewTripModal',true);
		},
		getMaxLoadingMeters(item) {
			if (item.cargoCarrier && item.cargoCarrier.loadingMeters)
				return item.cargoCarrier.loadingMeters;
			return undefined;
		},
		getMaxWeight(item) {
			if (item.cargoCarrier && item.cargoCarrier.weight)
				return item.cargoCarrier.weight;
			return undefined;
		},
		getMaxVolume(item) {
			if (item.cargoCarrier && item.cargoCarrier.volume)
				return item.cargoCarrier.volume;
			return undefined;
		},
		showLoadingList() {
			let items = this.$refs.grid.getSelectedItems();
			let ids = items.map(x => x.id);
			this.getLoadingList({ tripIds: ids });
		},
		showLoadingListLandscape() {
			let items = this.$refs.grid.getSelectedItems();
			let ids = items.map(x => x.id);
			this.getLoadingList({ tripIds: ids, landscape: true });
		},
	},
};
</script>

<style lang="scss" scoped>
.trip-list {
	.trip-list-header {
		height: 100%;
		display:flex;
		align-items: center;
		padding-left: 15px;
	}
	.button-group-left{
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: flex-end;
		> button:not(:first-child) {
			margin-left: 5px;
		}
	}
	.button-group-right {
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: flex-end;
		> button {
			margin-left: 10px;
		}
	}
	.trip-list-summary {
		display:grid;
		padding: 15px;
		grid-template-columns: auto repeat(3, max-content);
		grid-column-gap: 20px;
		margin-left: auto;
	}
	.trip-list-sub-header {
		display:flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: space-between;
	}
	.trip-list-content {
		height: 100%;
		padding: 0px 5px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.cargo-pane {
		margin: 0 20px;
	}
}

</style>

